import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, LogOut, Upload, FileText, Loader2, Image, X, GripVertical } from 'lucide-react';
import { TemplateThumbnail } from '../components/TemplateThumbnail';
import { CanvaPanel } from '../components/CanvaPanel';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, useSortable, horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ──────────── Sortable showcase card ────────────
const SortableShowcaseItem = ({ template }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: template.template_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex-shrink-0 w-44 bg-white border border-violet-300 rounded-xl overflow-hidden shadow-sm select-none"
      data-testid={`showcase-sortable-${template.template_id}`}
    >
      <div className="relative aspect-[4/5] bg-gray-50 flex items-center justify-center overflow-hidden">
        {template.canvas_data ? (
          <TemplateThumbnail canvasData={template.canvas_data} width={170} />
        ) : (
          <FileText className="h-8 w-8 text-gray-300" />
        )}
        <button
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 p-1.5 rounded-md bg-white/90 border border-gray-200 cursor-grab active:cursor-grabbing shadow-sm hover:bg-white"
          data-testid={`drag-handle-${template.template_id}`}
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4 text-gray-500" />
        </button>
      </div>
      <div className="p-2.5">
        <p className="text-xs font-semibold tracking-tight truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{template.name}</p>
        <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {template.type}
        </p>
      </div>
    </div>
  );
};

export const AdminTemplatesPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState('listing');
  const [type, setType] = useState('post');
  const [thumbnailUrl, setThumbnailUrl] = useState('');

  // PDF upload state
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [pdfCategory, setPdfCategory] = useState('listing');
  const [pdfType, setPdfType] = useState('post');
  const [pdfPage, setPdfPage] = useState(0);
  const [pdfUploading, setPdfUploading] = useState(false);
  const pdfInputRef = useRef(null);

  // Redirect non-admin users (admin OR owner allowed)
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'owner') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/templates');
      setTemplates(response.data.templates);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setName('');
    setCategory('listing');
    setType('post');
    setThumbnailUrl('');
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setName(template.name);
    setCategory(template.category);
    setType(template.type);
    setThumbnailUrl(template.thumbnail_url || '');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Pick a sensible default canvas size per template type.
    // (Same numbers the live editor uses in CanvasDesignerPage.)
    const CANVAS_SIZES = {
      post:       { width: 1080, height: 1080 },
      story:      { width: 1080, height: 1920 },
      reel:       { width: 1080, height: 1920 },
      carousel:   { width: 1080, height: 1080 },
      newsletter: { width: 600,  height: 800  },
      flyer:      { width: 1275, height: 1650 }, // 8.5x11 @ 150dpi
      postcard:   { width: 900,  height: 600  }, // 6x4   @ 150dpi
      brochure:   { width: 1650, height: 1275 }, // 11x8.5 @ 150dpi
    };
    const size = CANVAS_SIZES[type] || CANVAS_SIZES.post;

    const templateData = {
      name,
      category,
      type,
      thumbnail_url: thumbnailUrl || null,
      canvas_data: {
        width: size.width,
        height: size.height,
        backgroundColor: '#FFFFFF',
        elements: [],
      },
    };

    try {
      if (editingTemplate) {
        await apiClient.put(`/templates/${editingTemplate.template_id}`, templateData);
        toast.success('Template updated successfully');
        setDialogOpen(false);
        fetchTemplates();
      } else {
        const res = await apiClient.post('/templates', templateData);
        const newId = res.data?.template_id || res.data?.id;
        toast.success('Template created — opening editor');
        setDialogOpen(false);
        // Jump straight into the Canvas Designer for the new template so the
        // admin can actually design it instead of being left on the list page.
        if (newId) {
          navigate(`/canvas-designer/${newId}?returnTo=/admin/templates`);
        } else {
          fetchTemplates();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await apiClient.delete(`/templates/${templateId}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const toggleShowcase = async (templateId, value) => {
    // Optimistic update so the checkbox feels instant
    setTemplates(prev => prev.map(t =>
      t.template_id === templateId ? { ...t, is_showcase: value } : t
    ));
    try {
      await apiClient.patch(`/templates/${templateId}/showcase`, { is_showcase: value });
      toast.success(value ? 'Added to public demo' : 'Removed from public demo');
    } catch (error) {
      // Revert on failure
      setTemplates(prev => prev.map(t =>
        t.template_id === templateId ? { ...t, is_showcase: !value } : t
      ));
      toast.error('Failed to update showcase flag');
    }
  };

  const handlePdfUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile || !pdfName.trim()) {
      toast.error('Please select a PDF file and enter a name');
      return;
    }
    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      formData.append('name', pdfName);
      formData.append('category', pdfCategory);
      formData.append('template_type', pdfType);
      formData.append('page_number', pdfPage.toString());

      const response = await apiClient.post('/admin/upload-pdf-template', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      toast.success(`Template created with ${response.data.element_count} elements!`);
      setPdfDialogOpen(false);
      setPdfFile(null);
      setPdfName('');
      fetchTemplates();
      // Navigate to the canvas designer to review
      if (response.data.template_id) {
        navigate(`/canvas-designer/${response.data.template_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert PDF');
    } finally {
      setPdfUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-texture" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glassmorphic">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-logo">RealProp Admin</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">Welcome, {user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b border-border/50">
        <div className="container mx-auto px-6">
          <div className="flex gap-6 py-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground" data-testid="nav-dashboard">Dashboard</Link>
            <Link to="/admin/templates" className="text-primary font-medium" data-testid="nav-admin-templates">Manage Templates</Link>
            <Link to="/templates" className="text-muted-foreground hover:text-foreground" data-testid="nav-templates">User View</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Quick Nav to other template managers */}
        <div className="flex gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/reel-templates')} data-testid="go-reel-templates">
            Reel Templates
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/carousel-templates')} data-testid="go-carousel-templates">
            Carousel Templates
          </Button>
        </div>

        {/* Canva integration */}
        <section className="mb-10" data-testid="canva-section">
          <div className="flex items-end justify-between mb-3">
            <div>
              <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>From Canva</h3>
              <p className="text-sm text-muted-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Import any of your existing Canva designs as a RealProp template in one click.
              </p>
            </div>
          </div>
          <CanvaPanel onImported={() => fetchTemplates && fetchTemplates()} />
        </section>

        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-title">Template Management</h2>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/canvas-designer')} data-testid="visual-editor-button">
              Design in Editor
            </Button>

            {/* PDF Upload Dialog */}
            <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={() => setPdfDialogOpen(true)} data-testid="upload-pdf-button">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload PDF
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" /> Convert PDF to Template
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePdfUpload} className="space-y-4" data-testid="pdf-upload-form">
                  <div>
                    <Label>PDF File</Label>
                    <div
                      className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => pdfInputRef.current?.click()}
                      data-testid="pdf-drop-zone"
                    >
                      {pdfFile ? (
                        <div className="space-y-1">
                          <FileText className="h-8 w-8 mx-auto text-primary" />
                          <p className="text-sm font-medium">{pdfFile.name}</p>
                          <p className="text-xs text-muted-foreground">{(pdfFile.size / 1024 / 1024).toFixed(1)} MB</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">Click to select a PDF</p>
                          <p className="text-xs text-muted-foreground">Canva, InDesign, or any PDF design</p>
                        </div>
                      )}
                    </div>
                    <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden"
                      onChange={(e) => {
                        const f = e.target.files[0];
                        if (f) { setPdfFile(f); if (!pdfName) setPdfName(f.name.replace('.pdf', '')); }
                      }}
                      data-testid="pdf-file-input"
                    />
                  </div>

                  <div>
                    <Label>Template Name</Label>
                    <Input value={pdfName} onChange={(e) => setPdfName(e.target.value)}
                      required placeholder="My Design Template" data-testid="pdf-name-input" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Category</Label>
                      <Select value={pdfCategory} onValueChange={setPdfCategory}>
                        <SelectTrigger data-testid="pdf-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="listing">New Listing</SelectItem>
                          <SelectItem value="under_contract">Under Contract</SelectItem>
                          <SelectItem value="sold">Sold</SelectItem>
                          <SelectItem value="price_adjustment">Price Adjustment</SelectItem>
                          <SelectItem value="festive">Festive/Holiday</SelectItem>
                          <SelectItem value="market_stats">Market Stats</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={pdfType} onValueChange={setPdfType}>
                        <SelectTrigger data-testid="pdf-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="post">Social Post (1080x1080)</SelectItem>
                          <SelectItem value="story">Story (1080x1920)</SelectItem>
                          <SelectItem value="carousel">Carousel (1080x1080)</SelectItem>
                          <SelectItem value="newsletter">Newsletter (600x800)</SelectItem>
                          <SelectItem value="flyer">Flyer (8.5x11)</SelectItem>
                          <SelectItem value="postcard">Postcard (6x4)</SelectItem>
                          <SelectItem value="brochure">Brochure (11x8.5)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Page Number (0-indexed)</Label>
                    <Input type="number" min="0" value={pdfPage}
                      onChange={(e) => setPdfPage(parseInt(e.target.value) || 0)}
                      data-testid="pdf-page-input" />
                    <p className="text-xs text-muted-foreground mt-1">For multi-page PDFs, select which page to convert</p>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setPdfDialogOpen(false)}
                      disabled={pdfUploading}>Cancel</Button>
                    <Button type="submit" disabled={!pdfFile || pdfUploading} data-testid="pdf-submit-button">
                      {pdfUploading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Converting...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> Convert to Template</>
                      )}
                    </Button>
                  </div>

                  {pdfUploading && (
                    <p className="text-xs text-center text-muted-foreground">
                      AI is analyzing your design. This may take 15-30 seconds...
                    </p>
                  )}
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} data-testid="create-template-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Quick Create
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="template-form">
                  <div>
                    <Label htmlFor="name">Template Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Modern Listing Post"
                      data-testid="template-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="listing">New Listing</SelectItem>
                        <SelectItem value="under_contract">Under Contract</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                        <SelectItem value="price_adjustment">Price Adjustment</SelectItem>
                        <SelectItem value="festive">Festive/Holiday</SelectItem>
                        <SelectItem value="market_stats">Market Stats</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="type">Type</Label>
                    <Select value={type} onValueChange={setType}>
                      <SelectTrigger data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="post">Social Media Post</SelectItem>
                        <SelectItem value="story">Instagram/FB Story</SelectItem>
                        <SelectItem value="reel">Video Reel</SelectItem>
                        <SelectItem value="carousel">Carousel</SelectItem>
                        <SelectItem value="newsletter">Newsletter</SelectItem>
                        <SelectItem value="flyer">Flyer</SelectItem>
                        <SelectItem value="postcard">Postcard</SelectItem>
                        <SelectItem value="brochure">Brochure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Thumbnail / Preview</Label>
                    <div className="mt-2 space-y-2">
                      {/* Upload area */}
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                        onClick={() => { const el = document.getElementById('admin-thumb-upload'); if(el) el.click(); }}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                        onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                          const file = e.dataTransfer.files[0];
                          if (!file || !file.type.startsWith('image/')) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const res = await apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                            setThumbnailUrl(res.data.url);
                            toast.success('Thumbnail uploaded');
                          } catch { toast.error('Upload failed'); }
                        }}
                        data-testid="admin-thumb-dropzone"
                      >
                        {thumbnailUrl ? (
                          <div className="relative inline-block">
                            <img src={thumbnailUrl} alt="Preview" className="max-h-32 mx-auto rounded-lg" />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setThumbnailUrl(''); }}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <Image className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500 font-medium">Click or drag & drop thumbnail</p>
                            <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                          </>
                        )}
                      </div>
                      <input
                        id="admin-thumb-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const res = await apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                            setThumbnailUrl(res.data.url);
                            toast.success('Thumbnail uploaded');
                          } catch { toast.error('Upload failed'); }
                          e.target.value = '';
                        }}
                      />
                      {/* Or paste URL */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">or</span>
                        <Input
                          value={thumbnailUrl}
                          onChange={(e) => setThumbnailUrl(e.target.value)}
                          placeholder="Paste image URL..."
                          className="text-sm"
                          data-testid="thumbnail-url-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="save-template-button">
                      {editingTemplate ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-20">
            <p>Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">No templates yet. Create your first template!</p>
          </div>
        ) : (
          <>
            <ShowcaseReorderSection templates={templates} setTemplates={setTemplates} />
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="templates-grid">
            {templates.map((template) => (
              <div
                key={template.template_id}
                className="border border-border/50 bg-card rounded-lg overflow-hidden"
                data-testid={`template-card-${template.template_id}`}
              >
              <div className="bg-secondary/10 flex items-center justify-center overflow-hidden relative">
                  {template.is_showcase && (
                    <span
                      className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-bold uppercase tracking-wider shadow-md"
                      data-testid={`showcase-badge-${template.template_id}`}
                    >
                      ★ Demo
                    </span>
                  )}
                  {template.canvas_data ? (
                    <TemplateThumbnail canvasData={template.canvas_data} width={280} />
                  ) : template.thumbnail_url ? (
                    <img src={template.thumbnail_url} alt={template.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="aspect-video flex items-center justify-center w-full">
                      <span className="text-4xl text-muted-foreground">
                        {template.source === 'pdf_upload' ? <FileText className="h-10 w-10" /> : ''}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold mb-1">{template.name}</h3>
                  <p className="text-sm text-muted-foreground capitalize mb-3">
                    {(template.category || 'general').replace('_', ' ')} • {template.type || 'post'}
                  </p>
                  <label className="flex items-center gap-2 mb-3 cursor-pointer select-none text-sm" data-testid={`showcase-toggle-${template.template_id}`}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      checked={!!template.is_showcase}
                      onChange={(e) => toggleShowcase(template.template_id, e.target.checked)}
                      data-testid={`showcase-checkbox-${template.template_id}`}
                    />
                    <span className="text-gray-700">Show in public demo</span>
                  </label>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => navigate(`/canvas-designer/${template.template_id}`)}
                      data-testid={`design-template-${template.template_id}`}
                    >
                      Design
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(template)}
                      data-testid={`edit-template-${template.template_id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(template.template_id)}
                      data-testid={`delete-template-${template.template_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </main>
    </div>
  );
};

// ──────────── Showcase Reorder Section ────────────
const ShowcaseReorderSection = ({ templates, setTemplates }) => {
  // Always show as a sortable strip of showcase templates, sorted by showcase_order
  const showcaseList = useMemo(() => {
    return templates
      .filter(t => t.is_showcase)
      .slice()
      .sort((a, b) => (a.showcase_order ?? 999) - (b.showcase_order ?? 999));
  }, [templates]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (showcaseList.length === 0) {
    return (
      <div className="mb-6 p-5 rounded-2xl border border-dashed border-violet-300 bg-violet-50/30" data-testid="showcase-empty-hint">
        <p className="text-sm font-semibold tracking-tight text-violet-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Demo lineup is auto-picked
        </p>
        <p className="text-xs text-violet-700/80 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tick "Show in public demo" on any template below to start curating the public <code>/demo</code> page. Once you mark 2+ templates here, drag them to set the display order.
        </p>
      </div>
    );
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = showcaseList.findIndex(t => t.template_id === active.id);
    const newIndex = showcaseList.findIndex(t => t.template_id === over.id);
    const reordered = arrayMove(showcaseList, oldIndex, newIndex);
    // Optimistic: assign showcase_order locally then persist
    const orderedIds = reordered.map(t => t.template_id);
    setTemplates(prev => prev.map(t => {
      const idx = orderedIds.indexOf(t.template_id);
      return idx >= 0 ? { ...t, showcase_order: idx } : t;
    }));
    try {
      await apiClient.post('/templates/showcase/reorder', { template_ids: orderedIds });
      toast.success('Demo order updated');
    } catch {
      toast.error('Failed to save order');
    }
  };

  return (
    <div className="mb-8 p-5 rounded-2xl border border-violet-300 bg-gradient-to-br from-violet-50/60 to-white" data-testid="showcase-reorder-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold tracking-tight text-violet-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Public Demo Lineup
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-200/60 text-violet-700 text-[10px] font-bold uppercase tracking-wider">
              {showcaseList.length} curated
            </span>
          </p>
          <p className="text-xs text-violet-700/80 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Drag the cards to set the order shown on the public <code>/demo</code> page (top-left first).
          </p>
        </div>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={showcaseList.map(t => t.template_id)} strategy={horizontalListSortingStrategy}>
          <div className="flex gap-3 overflow-x-auto pb-2" data-testid="showcase-strip">
            {showcaseList.map(t => (
              <SortableShowcaseItem key={t.template_id} template={t} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};